import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const currentLang = i18n.language.startsWith('es') ? 'es' : 'en';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { label: t('nav.home'), to: '/' },
    { label: t('nav.about'), to: '/about' },
    { label: t('nav.services'), to: '/services' },
    { label: t('nav.consulting'), to: '/business-consulting' },
    { label: t('nav.products'), to: '/products' },
    { label: t('nav.contact'), to: '/contact' },
  ];

  const toggleLanguage = () => {
    const newLang = currentLang === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
  };

  const isActive = (to: string) => location.pathname === to;

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-purple-900/20' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 flex-shrink-0">
          <img
            src="https://static.readdy.ai/image/3aca7b4f25b4386bcee0afd7be9c4116/bf9b12c14ca0efd0a2bc13fcb2a88875.png"
            alt="Smart Business Digital Solutions"
            className="h-12 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium tracking-wide transition-all duration-300 relative group whitespace-nowrap ${
                isActive(link.to) ? 'text-[#8c52ff]' : 'text-white/80 hover:text-white'
              }`}
            >
              {link.label}
              <span
                className={`absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] transition-all duration-300 ${
                  isActive(link.to) ? 'w-full' : 'w-0 group-hover:w-full'
                }`}
              />
            </Link>
          ))}
        </div>

        {/* Right Side */}
        <div className="hidden md:flex items-center gap-4">
          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="text-xs font-bold text-white/60 hover:text-white border border-white/20 hover:border-white/40 rounded-full px-3 py-1.5 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {currentLang === 'es' ? 'EN' : 'ES'}
          </button>
          
          <a
            href="https://smartbusiness.academy"
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap text-sm font-semibold text-white/80 hover:text-white px-3 py-2 transition-all duration-300 cursor-pointer"
          >
            {t('nav.academy')}
          </a>

          {/* Auth Buttons */}
          {user ? (
            <div className="flex items-center gap-3">
              <Link
                to="/crm/dashboard"
                className="whitespace-nowrap text-sm font-semibold text-white bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] px-5 py-2.5 rounded-full hover:opacity-90 transition-all duration-300 cursor-pointer"
              >
                Dashboard
              </Link>
              <button
                onClick={handleLogout}
                className="text-white/60 hover:text-white text-sm font-medium transition-colors cursor-pointer"
              >
                Logout
              </button>
            </div>
          ) : null}
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 cursor-pointer p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
          <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
          <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-500 overflow-hidden ${
          menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        } bg-black/95 backdrop-blur-xl border-t border-white/10`}
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-base font-medium transition-colors duration-300 ${
                isActive(link.to) ? 'text-[#8c52ff]' : 'text-white/80'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="flex flex-col gap-3 pt-2 border-t border-white/10">
            <div className="flex items-center gap-3">
              <button
                onClick={toggleLanguage}
                className="text-xs font-bold text-white/60 border border-white/20 rounded-full px-3 py-1.5 cursor-pointer whitespace-nowrap"
              >
                {currentLang === 'es' ? 'EN' : 'ES'}
              </button>
              <a
                href="https://smartbusiness.academy"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap text-sm font-semibold text-white/80"
              >
                {t('nav.academy')}
              </a>
            </div>

            {/* Mobile Auth Buttons */}
            {user ? (
              <div className="flex flex-col gap-3 mt-2">
                <Link
                  to="/crm/dashboard"
                  className="text-center text-sm font-semibold text-white bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] px-5 py-2.5 rounded-full cursor-pointer"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-center text-white/60 hover:text-white text-sm font-medium transition-colors cursor-pointer"
                >
                  Logout
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
